import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Bot, Send, Loader2, Sparkles, User as UserIcon, Lock, Save, Hash } from "lucide-react";

const SAMPLES = [
  { lang: "EN", text: "Suggest 3 fun community events for a Discord gaming server." },
  { lang: "AR", text: "اعطني نصائح للعب فالورانت كمبتدئ" },
  { lang: "ES", text: "¿Cuáles son los mejores juegos cooperativos en 2026?" },
  { lang: "FR", text: "Donne-moi 3 idées de défis pour notre communauté." },
];

export default function GeminiChat() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `web-${Date.now()}`);
  const scrollRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (active?.id && active?.bot_present) {
      api.get(`/bot/settings?guild_id=${active.id}`).then((r) => setCfg(r.data));
    }
  }, [active?.id, active?.bot_present]);

  const aiLocked = active?.bot_present && active?.tier === "free";

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/gemini/chat", { message: text, session_id: sessionId, guild_id: active?.id || null });
      setMessages((m) => [...m, { role: "ai", text: data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "AI is offline.";
      toast.error(detail);
      setMessages((m) => [...m, { role: "ai", text: `⚠️ ${detail}` }]);
    } finally {
      setSending(false);
    }
  };

  const saveAiCfg = async () => {
    if (!cfg) return;
    setSavingCfg(true);
    try {
      await api.post(`/bot/settings?guild_id=${active.id}`, cfg);
      toast.success("AI auto-chat settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingCfg(false); }
  };

  return (
    <div data-testid="ai-panel" className="space-y-8 h-full flex flex-col">
      <div>
        <div className="overline mb-2">// GEMINI 3 FLASH</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">AI Chat</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">
          Multilingual assistant. Ask anything in any language — replies stay in the same one.
        </p>
      </div>

      {aiLocked && (
        <div className="tactical-card p-5 border-yellow-500/40 bg-yellow-500/5 flex items-start gap-4">
          <Lock className="w-6 h-6 text-yellow-400 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-heading text-lg font-bold uppercase">AI is locked on Free tier</div>
            <p className="text-sm text-zinc-400 mt-1">Upgrade {active.name} to Starter ($5/mo) or higher to unlock Gemini chat.</p>
          </div>
          <a href="/dashboard/pricing" className="btn-primary" data-testid="ai-upgrade-link">Upgrade</a>
        </div>
      )}

      {!aiLocked && cfg && active?.bot_present && (
        <div className="tactical-card p-6" data-testid="ai-autochat-config">
          <div className="overline mb-1">// AUTO-CHAT IN DISCORD</div>
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-1">Let the bot chat freely</h2>
          <p className="text-sm text-zinc-400 mb-5">
            Pick a channel where the bot will reply to <span className="text-white">every message</span>, or just let it
            answer when @mentioned anywhere. Replies are powered by Gemini 3 Flash and match the user's language.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="overline block mb-2 flex items-center gap-2"><Hash className="w-3 h-3" /> AI auto-chat channel</label>
              <select className="tactical-input" value={cfg.ai_channel_id || ""} onChange={(e) => setCfg({ ...cfg, ai_channel_id: e.target.value || null })} data-testid="ai-channel-select">
                <option value="">— None (mention only) —</option>
                {channels.filter((c) => c.type === "text").map((c) => (
                  <option key={c.id} value={c.id}>#{c.name}</option>
                ))}
              </select>
              <div className="font-mono-tabular text-[10px] text-zinc-600 mt-1">Bot replies to ALL messages in this channel</div>
            </div>
            <div>
              <label className="overline block mb-2">Respond when @mentioned</label>
              <button type="button" onClick={() => setCfg({ ...cfg, ai_respond_on_mention: !cfg.ai_respond_on_mention })}
                className={`w-full p-3 border flex items-center justify-between rounded-lg ${cfg.ai_respond_on_mention ? "border-brand-orange bg-brand-orange/5" : "border-brand-line"}`}
                data-testid="ai-mention-toggle">
                <span className="text-sm font-semibold">{cfg.ai_respond_on_mention ? "ENABLED" : "DISABLED"}</span>
                <span className={`w-10 h-5 relative rounded-full ${cfg.ai_respond_on_mention ? "bg-brand-orange" : "bg-brand-line"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-black rounded-full ${cfg.ai_respond_on_mention ? "left-5" : "left-0.5"}`} />
                </span>
              </button>
            </div>
          </div>
          <div className="mt-5">
            <label className="overline block mb-2">AI personality (system prompt)</label>
            <textarea className="tactical-input min-h-[90px]" value={cfg.ai_personality || ""}
              onChange={(e) => setCfg({ ...cfg, ai_personality: e.target.value })}
              placeholder="You are a friendly multilingual gaming bot..."
              data-testid="ai-personality" />
            <div className="font-mono-tabular text-[10px] text-zinc-600 mt-1">Define the bot's tone, language style, knowledge focus.</div>
          </div>
          <div className="flex justify-end mt-5">
            <button onClick={saveAiCfg} disabled={savingCfg} className="btn-primary" data-testid="ai-cfg-save">
              {savingCfg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Auto-Chat
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-4 min-h-[560px]">
        <div className="lg:col-span-3 tactical-card flex flex-col overflow-hidden">
          <div className="px-5 py-4 border-b border-brand-line flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-brand-orange/10 border border-brand-orange flex items-center justify-center rounded-lg">
                <Bot className="w-4 h-4 text-brand-orange" />
              </div>
              <div>
                <div className="font-heading text-base font-bold uppercase tracking-tight">606-AI</div>
                <div className="font-mono-tabular text-[10px] text-emerald-400">● ONLINE · GEMINI 3 FLASH</div>
              </div>
            </div>
            <span className="font-mono-tabular text-xs text-zinc-500">{messages.length} msgs</span>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto tactical-scroll p-5 space-y-4" data-testid="ai-message-list">
            {messages.length === 0 && (
              <div className="text-center py-16">
                <Sparkles className="w-10 h-10 text-brand-orange mx-auto mb-3" />
                <div className="font-heading uppercase tracking-tight text-lg">Start the conversation</div>
                <div className="text-sm text-zinc-500 mt-1">Try one of the prompts on the right.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "ai" && (
                  <div className="w-8 h-8 bg-brand-orange/10 border border-brand-orange flex items-center justify-center shrink-0 rounded-lg">
                    <Bot className="w-4 h-4 text-brand-orange" />
                  </div>
                )}
                <div className={`max-w-[80%] p-4 rounded-xl ${m.role === "user" ? "bg-brand-orange text-black" : "bg-brand-elevated border border-brand-line"}`} data-testid={`message-${i}`}>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 bg-white/10 border border-zinc-600 flex items-center justify-center shrink-0 rounded-lg">
                    <UserIcon className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-zinc-500 text-sm font-mono-tabular">
                <Loader2 className="w-4 h-4 animate-spin" /> AI is thinking…
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-brand-line p-4 flex items-center gap-3" data-testid="ai-form">
            <input className="tactical-input" placeholder="Type in any language…" value={input}
                   onChange={(e) => setInput(e.target.value)} disabled={sending || aiLocked} data-testid="ai-input" />
            <button type="submit" className="btn-primary" disabled={sending || aiLocked || !input.trim()} data-testid="ai-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </form>
        </div>

        <div className="tactical-card p-5">
          <div className="overline mb-3">// QUICK PROMPTS</div>
          <div className="space-y-2">
            {SAMPLES.map((s) => (
              <button key={s.text} onClick={() => send(s.text)} disabled={sending || aiLocked}
                      className="w-full text-left border border-brand-line p-3 hover:border-brand-orange transition-colors group rounded-lg disabled:opacity-50"
                      data-testid={`prompt-${s.lang}`}>
                <div className="overline text-zinc-400 group-hover:text-brand-orange transition-colors">{s.lang}</div>
                <div className="text-sm mt-1">{s.text}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
